import type { ComponentProps } from 'react'
import type { ListBoxItemProps, SelectProps } from 'react-aria-components'
import {
  composeRenderProps,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from 'react-aria-components'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'
import MaterialSymbolsCheck from '~icons/material-symbols/check'
import { UIButton } from './Button'

interface UISelectProps<T extends object>
  extends
    Omit<SelectProps<T>, 'children' | 'className' | 'style'>,
    StyleProps,
    Pick<VariantProps<typeof selectStyles>, 'variant'> {
  items?: Iterable<T>
  children: React.ReactNode | ((item: T) => React.ReactNode)
  labelSlot?: React.ReactNode

  buttonClassName?: string
  popoverClassName?: string
  selectValueClassName?: string
}

const selectStyles = tv({
  slots: {
    root: '',
    button: 'text-xs rounded-md border items-center',
    list: 'bg-background w-full rounded-md border-[1px] border-card-border gap-0.5 flex flex-col py-1',
    // Unset flex-1 of items in ListBox;
    selectValue: 'flex [&>span]:flex-initial',
  },
  variants: {
    variant: {
      filled: {
        button: '',
      },
    },
  },
})

export function UISelect<T extends object>(props: UISelectProps<T>) {
  const {
    children,
    items,
    labelSlot,
    buttonClassName,
    popoverClassName,
    selectValueClassName,
    ...otherProps
  } = props
  const styles = selectStyles({
    variant: props.variant,
  })

  return (
    <Select
      {...otherProps}
      className={styles.root({ className: props.className })}
    >
      {labelSlot}
      <UIButton
        variant="outlined"
        size="small"
        endIcon={<span aria-hidden="true">▼</span>}
        className={styles.button({ className: buttonClassName })}
      >
        <SelectValue
          className={styles.selectValue({ className: selectValueClassName })}
        />
      </UIButton>
      <Popover className={popoverClassName}>
        <ListBox className={styles.list()} items={items}>
          {children}
        </ListBox>
      </Popover>
    </Select>
  )
}

const selectItemStyles = tv({
  slots: {
    root: [
      'flex items-center outline-none py-1.5 px-2 text-xs gap-1',
      'hover:bg-neutral-50 dark:hover:bg-neutral-600 selected:bg-neutral-200 selected:dark:bg-neutral-700 overflow-hidden cursor-pointer selected:font-medium',
    ],
    label: 'flex-1',
    dropdownIcon: 'ml-1 invisible',
  },
  variants: {
    isSelected: {
      true: {
        dropdownIcon: 'visible',
      },
    },
  },
})

interface UISelectItemProps extends ListBoxItemProps {
  startIcon?: React.ReactNode
  className?: string
  labelClassName?: string
}

export function UISelectItem({
  startIcon,
  children,
  ...props
}: UISelectItemProps) {
  const styles = selectItemStyles()
  return (
    <ListBoxItem
      {...props}
      className={styles.root({ className: props.className })}
    >
      {composeRenderProps(children, (children, { isSelected }) => (
        <>
          {startIcon}
          <span className={styles.label({ className: props.labelClassName })}>
            {children}
          </span>

          <MaterialSymbolsCheck
            className={styles.dropdownIcon({ isSelected })}
          />
        </>
      ))}
    </ListBoxItem>
  )
}

const nativeStyle = tv({
  slots: {
    select: [
      // Slightly tweaked from UIButton
      'appearance-none pl-1.5 pr-5 py-0.5',
      'border outline-2 rounded-lg outline-offset-1 transition duration-75',
      // Light
      'hover:bg-black/5 border-black/50 focus:border-black/20 outline-black/80',
      'disabled:text-black/40 disabled:border-black/20',
      // Dark
      'dark:not-focus:hover:bg-white/5 dark:border-neutral-600 dark:focus:border-transparent dark:outline-neutral-400 dark:bg-background',
      'col-start-1 row-start-1',
    ],
    wrapper: [
      'z-1',
      'outline-none border-none',
      'grid grid-cols-1 grid-rows-1 items-center after:col-start-1 after:row-start-1 after:content-[""] after:w-2 after:h-1.5 after:bg-neutral-600 dark:after:bg-neutral-300 after:[clip-path:polygon(100%_0%,0_0%,50%_100%)] after:justify-self-end after:mr-2',
    ],
  },
})

export function UISelectNative(props: ComponentProps<'select'>) {
  const styles = nativeStyle()
  return (
    <div className={styles.wrapper({ className: props.className })}>
      <select {...props} className={styles.select()}>
        {props.children}
      </select>
    </div>
  )
}
